import { useState, useRef, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import * as XLSX from 'xlsx';

type StaffType = 'infirmier' | 'sage_femme' | 'secretaire' | 'laborantin' | 'comptable' | 'super_admin' | 'medecin';

interface BulkImportStaffModalProps {
  isOpen: boolean;
  onClose: () => void;
  staffType: StaffType;
  onSuccess: () => void;
}

interface ParsedRow {
  nom: string;
  prenom: string;
  email: string;
  motDePasse: string;
  sexe: string;
  telephone: string;
  date_naissance: string;
  specialite: string;
  numero_ordre: string;
}

const STAFF_LABELS: Record<StaffType, { label: string; article: string; icon: string }> = {
  infirmier: { label: 'Infirmiers', article: 'des', icon: 'ri-nurse-line' },
  sage_femme: { label: 'Sages-Femmes', article: 'des', icon: 'ri-nurse-line' },
  secretaire: { label: 'Secrétaires', article: 'des', icon: 'ri-user-settings-line' },
  laborantin: { label: 'Laborantins', article: 'des', icon: 'ri-microscope-line' },
  comptable: { label: 'Comptables', article: 'des', icon: 'ri-calculator-line' },
  super_admin: { label: 'Administrateurs', article: 'des', icon: 'ri-shield-user-line' },
  medecin: { label: 'Médecins', article: 'des', icon: 'ri-stethoscope-line' },
};

const REQUIRED_COLUMNS = ['nom', 'prenom', 'email', 'motDePasse'];
const OPTIONAL_COLUMNS = ['sexe', 'telephone', 'date_naissance', 'specialite', 'numero_ordre'];

function parseXLSX(data: ArrayBuffer): { headers: string[]; rows: string[][] } {
  const workbook = XLSX.read(new Uint8Array(data), { type: 'array' });
  const sheetName = workbook.SheetNames[0];
  if (!sheetName) return { headers: [], rows: [] };

  const worksheet = workbook.Sheets[sheetName];
  const rawRows = XLSX.utils.sheet_to_json(worksheet, { header: 1 }) as unknown[][];

  if (rawRows.length === 0) return { headers: [], rows: [] };

  const headers = (rawRows[0] as unknown[]).map((h) => String(h || '').toLowerCase().trim());
  const rows = rawRows
    .slice(1)
    .map((r) => (r as unknown[]).map((c) => String(c ?? '').trim()))
    .filter((r) => r.length > 0 && r.some((c) => c !== ''));

  return { headers, rows };
}

function normalizeHeader(h: string): string {
  const map: Record<string, string> = {
    nom: 'nom',
    name: 'nom',
    lastname: 'nom',
    last_name: 'nom',
    prenom: 'prenom',
    prenoms: 'prenom',
    prénom: 'prenom',
    firstname: 'prenom',
    first_name: 'prenom',
    email: 'email',
    mail: 'email',
    courriel: 'email',
    e_mail: 'email',
    motdepasse: 'motDePasse',
    mot_de_passe: 'motDePasse',
    password: 'motDePasse',
    mdp: 'motDePasse',
    sexe: 'sexe',
    genre: 'sexe',
    gender: 'sexe',
    telephone: 'telephone',
    tel: 'telephone',
    phone: 'telephone',
    contact: 'telephone',
    portable: 'telephone',
    mobile: 'telephone',
    datenaissance: 'date_naissance',
    date_naissance: 'date_naissance',
    date_de_naissance: 'date_naissance',
    naissance: 'date_naissance',
    birthdate: 'date_naissance',
    dateofbirth: 'date_naissance',
    specialite: 'specialite',
    specialité: 'specialite',
    specialty: 'specialite',
    specialite_medicale: 'specialite',
    numero_ordre: 'numero_ordre',
    numéro_ordre: 'numero_ordre',
    numeroordre: 'numero_ordre',
    num_ordre: 'numero_ordre',
    order_number: 'numero_ordre',
    n_ordre: 'numero_ordre',
  };
  return map[h] || h;
}

export default function BulkImportStaffModal({ isOpen, onClose, staffType, onSuccess }: BulkImportStaffModalProps) {
  const [step, setStep] = useState<'upload' | 'preview' | 'importing' | 'done'>('upload');
  const [file, setFile] = useState<File | null>(null);
  const [parsedRows, setParsedRows] = useState<ParsedRow[]>([]);
  const [parseWarnings, setParseWarnings] = useState<string[]>([]);
  const [importProgress, setImportProgress] = useState({ current: 0, total: 0 });
  const [importResults, setImportResults] = useState<{ created: number; existing: number; updated?: number; skipped: number; errors: string[] } | null>(null);
  const [error, setError] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dropRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  const info = STAFF_LABELS[staffType];

  const reset = useCallback(() => {
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
    setStep('upload');
    setFile(null);
    setParsedRows([]);
    setParseWarnings([]);
    setImportProgress({ current: 0, total: 0 });
    setImportResults(null);
    setError('');
  }, []);

  const handleClose = () => {
    reset();
    onClose();
  };

  const parseFile = (file: File) => {
    setFile(file);
    setError('');
    setParseWarnings([]);

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = e.target?.result as ArrayBuffer;
        if (!data || data.byteLength === 0) {
          setError('Le fichier est vide');
          return;
        }

        const { headers, rows } = parseXLSX(data);

        if (headers.length === 0) {
          setError('Aucun en-tête trouvé. Vérifiez que la première ligne contient les noms des colonnes.');
          return;
        }

        const normalizedHeaders = headers.map(normalizeHeader);
        const missingColumns = REQUIRED_COLUMNS.filter((col) => !normalizedHeaders.includes(col));

        if (missingColumns.length > 0) {
          setError(`Colonnes obligatoires manquantes : ${missingColumns.join(', ')}. Colonnes trouvées : ${headers.join(', ')}`);
          return;
        }

        if (rows.length === 0) {
          setError('Aucune ligne de données trouvée dans le fichier.');
          return;
        }

        const errors: string[] = [];
        const warnings: string[] = [];
        const parsed: ParsedRow[] = [];

        // Détection des emails en double dans le fichier
        const emailCount = new Map<string, number[]>();
        rows.forEach((row, index) => {
          const obj: Record<string, string> = {};
          normalizedHeaders.forEach((h, i) => {
            obj[h] = (row[i] || '').replace(/^["']|["']$/g, '');
          });
          const emailLower = (obj.email || '').toLowerCase().trim();
          if (emailLower) {
            if (!emailCount.has(emailLower)) emailCount.set(emailLower, []);
            emailCount.get(emailLower)!.push(index);
          }
        });

        // Repérer les doublons
        const duplicateRowNums = new Set<number>();
        const duplicateEmailsList: string[] = [];
        emailCount.forEach((rowIndices, email) => {
          if (rowIndices.length > 1) {
            duplicateEmailsList.push(email);
            rowIndices.slice(1).forEach((ri) => duplicateRowNums.add(ri));
          }
        });

        rows.forEach((row, index) => {
          const obj: Record<string, string> = {};
          normalizedHeaders.forEach((h, i) => {
            obj[h] = (row[i] || '').replace(/^["']|["']$/g, '');
          });

          const rowNum = index + 2;

          if (!obj.nom || !obj.prenom || !obj.email || !obj.motDePasse) {
            errors.push(`Ligne ${rowNum} : champs obligatoires manquants`);
            return;
          }

          if (!obj.email.includes('@')) {
            errors.push(`Ligne ${rowNum} : email invalide "${obj.email}"`);
            return;
          }

          if (obj.motDePasse.length < 10) {
            errors.push(`Ligne ${rowNum} : mot de passe trop court (min 10 caracteres)`);
            return;
          }

          // Si cet email est en double dans le fichier, on ne garde que la première occurrence
          if (duplicateRowNums.has(index)) {
            warnings.push(`Ligne ${rowNum} : email "${obj.email}" en double dans le fichier — ignoré (seule la première occurrence est conservée)`);
            return;
          }

          parsed.push({
            nom: obj.nom || '',
            prenom: obj.prenom || '',
            email: obj.email || '',
            motDePasse: obj.motDePasse || '',
            sexe: obj.sexe || '',
            telephone: obj.telephone || '',
            date_naissance: obj.date_naissance || '',
            specialite: obj.specialite || '',
            numero_ordre: obj.numero_ordre || '',
          });
        });

        setParsedRows(parsed);
        setParseWarnings(warnings);

        if (parsed.length === 0 && errors.length > 0) {
          setError(`Aucune ligne valide. ${errors.length} erreur(s) trouvée(s).`);
        } else {
          setStep('preview');
        }
      } catch (err: any) {
        setError('Erreur lors de la lecture du fichier. Vérifiez qu\'il s\'agit bien d\'un fichier Excel (.xlsx) valide.');
      }
    };

    reader.onerror = () => {
      setError('Erreur lors de la lecture du fichier.');
    };

    reader.readAsArrayBuffer(file);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) parseFile(f);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const f = e.dataTransfer.files?.[0];
    if (f) parseFile(f);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleImport = async () => {
    if (parsedRows.length === 0) return;

    setStep('importing');
    setImportProgress({ current: 0, total: parsedRows.length });
    setError('');

    // Creer un AbortController pour pouvoir annuler
    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;
      if (!token) throw new Error('Session expiree');

      const CHUNK_SIZE = 20;
      const allErrors: string[] = [];
      let totalCreated = 0;
      let totalExisting = 0;
      let totalSkipped = 0;

      for (let i = 0; i < parsedRows.length; i += CHUNK_SIZE) {
        // Verifier si on a annule
        if (controller.signal.aborted) {
          break;
        }

        const chunk = parsedRows.slice(i, i + CHUNK_SIZE);
        const response = await supabase.functions.invoke('bulk-create-staff', {
          body: { type: staffType, staff: chunk },
          headers: { Authorization: `Bearer ${token}` },
        });

        if (response.error) {
          allErrors.push(`Lot ${Math.floor(i / CHUNK_SIZE) + 1} : ${response.error.message || 'Erreur'}`);
          totalSkipped += chunk.length;
        } else {
          const data = response.data;
          totalCreated += data?.created || 0;
          totalExisting += data?.existing || 0;
          totalSkipped += data?.skipped || 0;
          if (data?.errors?.length) {
            allErrors.push(...data.errors);
          }
        }

        setImportProgress({ current: Math.min(i + CHUNK_SIZE, parsedRows.length), total: parsedRows.length });
      }

      if (controller.signal.aborted) {
        // Import annule, on affiche ce qu'on a deja traite
        setImportResults({
          created: totalCreated,
          existing: totalExisting,
          skipped: totalSkipped + (parsedRows.length - importProgress.current),
          errors: [...allErrors, `Import annule par l'utilisateur apres ${importProgress.current}/${parsedRows.length} comptes.`],
        });
      } else {
        setImportResults({
          created: totalCreated,
          existing: totalExisting,
          skipped: totalSkipped,
          errors: allErrors,
        });
      }
      setStep('done');
    } catch (err: any) {
      if (err?.name === 'AbortError' || controller.signal.aborted) {
        setError('Import annule.');
        setStep('preview');
      } else {
        setError(err.message || 'Erreur lors de l\'import');
        setStep('preview');
      }
    } finally {
      abortRef.current = null;
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={handleClose} />
      <div className="relative bg-white rounded-3xl shadow-2xl max-w-4xl w-full mx-4 max-h-[90vh] flex flex-col z-10">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-teal-100 rounded-full flex items-center justify-center">
              <i className={`${info.icon} text-xl text-teal-600`}></i>
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Importer {info.article} {info.label}</h2>
              <p className="text-sm text-gray-500">Importez un fichier Excel (.xlsx) pour créer plusieurs comptes en une fois</p>
            </div>
          </div>
          <button onClick={handleClose} className="w-10 h-10 rounded-full hover:bg-gray-100 flex items-center justify-center transition-colors cursor-pointer">
            <i className="ri-close-line text-xl text-gray-500"></i>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-600 rounded-2xl flex items-start">
              <i className="ri-error-warning-line mr-2 mt-0.5 w-5 h-5 flex items-center justify-center flex-shrink-0"></i>
              <span className="text-sm">{error}</span>
            </div>
          )}

          {/* Step 1: Upload */}
          {step === 'upload' && (
            <div>
              <div className="mb-6 p-5 bg-teal-50 border border-teal-200 rounded-2xl">
                <h3 className="font-semibold text-teal-800 mb-3 flex items-center gap-2">
                  <i className="ri-information-line w-5 h-5 flex items-center justify-center"></i>
                  Format du fichier attendu
                </h3>
                <div className="text-sm text-teal-700 space-y-2">
                  <p>Le fichier doit être au format <strong>Excel (.xlsx)</strong> avec la première ligne contenant les en-têtes.</p>
                  <div>
                    <p className="font-medium mb-1">Colonnes obligatoires :</p>
                    <code className="bg-teal-100 px-2 py-1 rounded text-xs">nom, prenom, email, motDePasse</code>
                  </div>
                  <div>
                    <p className="font-medium mb-1">Colonnes optionnelles :</p>
                    <code className="bg-teal-100 px-2 py-1 rounded text-xs">sexe, telephone, date_naissance</code>
                    {(staffType === 'medecin' || staffType === 'infirmier' || staffType === 'laborantin') && (
                      <code className="bg-teal-100 px-2 py-1 rounded text-xs ml-1">, specialite</code>
                    )}
                    {staffType === 'medecin' && (
                      <code className="bg-teal-100 px-2 py-1 rounded text-xs ml-1">, numero_ordre</code>
                    )}
                  </div>
                  <p className="text-xs text-teal-600 mt-2">
                    Exemple : <code className="bg-white px-1 rounded">nom,prenom,email,motDePasse,sexe,telephone,date_naissance</code>
                  </p>
                </div>
              </div>

              <div
                ref={dropRef}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onClick={() => fileInputRef.current?.click()}
                className={`border-2 border-dashed rounded-2xl p-12 text-center cursor-pointer transition-all ${
                  isDragging
                    ? 'border-teal-500 bg-teal-50'
                    : 'border-gray-300 hover:border-teal-400 hover:bg-gray-50'
                }`}
              >
                <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-upload-cloud-2-line text-3xl text-teal-600"></i>
                </div>
                <p className="text-lg font-semibold text-gray-700 mb-2">
                  Glissez-déposez votre fichier Excel (.xlsx) ici
                </p>
                <p className="text-sm text-gray-500 mb-4">ou cliquez pour parcourir</p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleFileChange}
                  className="hidden"
                />
                <span className="inline-block px-6 py-2 bg-teal-600 text-white rounded-full text-sm font-medium hover:bg-teal-700 transition-colors whitespace-nowrap">
                  Parcourir les fichiers
                </span>
              </div>
            </div>
          )}

          {/* Step 2: Preview */}
          {step === 'preview' && (
            <div>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="font-semibold text-gray-900">Aperçu des données</h3>
                  <p className="text-sm text-gray-500">
                    {parsedRows.length} ligne(s) prête(s) à être importée(s)
                    {parseWarnings.length > 0 && (
                      <span className="text-amber-600 ml-2">({parseWarnings.length} avertissement(s))</span>
                    )}
                  </p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => { setStep('upload'); setFile(null); setParsedRows([]); setParseWarnings([]); }}
                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-full hover:bg-gray-200 font-medium transition-all text-sm whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-arrow-left-line mr-1"></i>Retour
                  </button>
                  <button
                    onClick={handleImport}
                    className="px-6 py-2 bg-teal-600 text-white rounded-full hover:bg-teal-700 font-semibold transition-all text-sm whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-upload-line mr-1"></i>Importer {parsedRows.length} compte(s)
                  </button>
                </div>
              </div>

              {parseWarnings.length > 0 && (
                <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-xl max-h-32 overflow-y-auto">
                  {parseWarnings.map((warn, i) => (
                    <p key={i} className="text-xs text-amber-700 flex items-start">
                      <i className="ri-error-warning-line mr-1 mt-0.5 w-4 h-4 flex items-center justify-center flex-shrink-0"></i>
                      {warn}
                    </p>
                  ))}
                </div>
              )}

              <div className="overflow-x-auto border border-gray-200 rounded-xl">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="text-left py-3 px-3 font-semibold text-gray-600 text-xs">#</th>
                      <th className="text-left py-3 px-3 font-semibold text-gray-600 text-xs">Nom</th>
                      <th className="text-left py-3 px-3 font-semibold text-gray-600 text-xs">Prénom</th>
                      <th className="text-left py-3 px-3 font-semibold text-gray-600 text-xs">Email</th>
                      <th className="text-left py-3 px-3 font-semibold text-gray-600 text-xs">Sexe</th>
                      <th className="text-left py-3 px-3 font-semibold text-gray-600 text-xs">Téléphone</th>
                      <th className="text-left py-3 px-3 font-semibold text-gray-600 text-xs">Date naiss.</th>
                      {(staffType === 'medecin' || staffType === 'infirmier' || staffType === 'laborantin') && (
                        <th className="text-left py-3 px-3 font-semibold text-gray-600 text-xs">Spécialité</th>
                      )}
                      {staffType === 'medecin' && (
                        <th className="text-left py-3 px-3 font-semibold text-gray-600 text-xs">N° Ordre</th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {parsedRows.slice(0, 50).map((row, i) => (
                      <tr key={i} className={`border-t border-gray-100 ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}`}>
                        <td className="py-2 px-3 text-gray-400 text-xs">{i + 1}</td>
                        <td className="py-2 px-3 font-medium text-gray-800">{row.nom}</td>
                        <td className="py-2 px-3 text-gray-700">{row.prenom}</td>
                        <td className="py-2 px-3 text-gray-600 text-xs">{row.email}</td>
                        <td className="py-2 px-3 text-gray-600 text-xs">{row.sexe || '-'}</td>
                        <td className="py-2 px-3 text-gray-600 text-xs">{row.telephone || '-'}</td>
                        <td className="py-2 px-3 text-gray-600 text-xs">{row.date_naissance || '-'}</td>
                        {(staffType === 'medecin' || staffType === 'infirmier' || staffType === 'laborantin') && (
                          <td className="py-2 px-3 text-gray-600 text-xs">{row.specialite || '-'}</td>
                        )}
                        {staffType === 'medecin' && (
                          <td className="py-2 px-3 text-gray-600 text-xs">{row.numero_ordre || '-'}</td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
                {parsedRows.length > 50 && (
                  <div className="text-center py-3 text-xs text-gray-400 bg-gray-50 border-t border-gray-100">
                    Affichage des 50 premières lignes sur {parsedRows.length}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Step 3: Importing */}
          {step === 'importing' && (
            <div className="text-center py-12">
              <div className="w-20 h-20 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-loader-4-line text-4xl text-teal-600 animate-spin"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Importation en cours...</h3>
              <p className="text-gray-500 mb-6">
                {importProgress.current} / {importProgress.total} comptes traités
              </p>
              <div className="max-w-md mx-auto bg-gray-200 rounded-full h-3 overflow-hidden mb-4">
                <div
                  className="h-full bg-teal-600 rounded-full transition-all duration-300"
                  style={{ width: `${importProgress.total > 0 ? Math.round((importProgress.current / importProgress.total) * 100) : 0}%` }}
                />
              </div>
              <p className="text-sm text-gray-400 mb-6">
                {importProgress.total > 0 ? Math.round((importProgress.current / importProgress.total) * 100) : 0}%
              </p>
              <button
                onClick={reset}
                className="px-6 py-2 bg-red-50 text-red-600 border border-red-200 rounded-full hover:bg-red-100 font-medium transition-all text-sm whitespace-nowrap cursor-pointer"
              >
                <i className="ri-close-circle-line mr-2"></i>Annuler l'importation
              </button>
            </div>
          )}

          {/* Step 4: Done */}
          {step === 'done' && importResults && (
            <div>
              <div className="text-center mb-6">
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-check-line text-4xl text-green-600"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Importation terminée !</h3>
                <p className="text-gray-500">
                  {importResults.created} créé(s), {importResults.existing} déjà existant(s) ignoré(s), {importResults.skipped} erreur(s)
                </p>
              </div>

              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="bg-green-50 rounded-2xl p-4 text-center">
                  <div className="text-3xl font-bold text-green-700">{importResults.created}</div>
                  <div className="text-sm text-green-600 mt-1">Créés</div>
                </div>
                <div className="bg-orange-50 rounded-2xl p-4 text-center">
                  <div className="text-3xl font-bold text-orange-700">{importResults.existing}</div>
                  <div className="text-sm text-orange-600 mt-1">Déjà existants</div>
                </div>
                <div className="bg-red-50 rounded-2xl p-4 text-center">
                  <div className="text-3xl font-bold text-red-700">{importResults.skipped}</div>
                  <div className="text-sm text-red-600 mt-1">Erreurs</div>
                </div>
              </div>

              {importResults.errors.length > 0 && (
                <div className="mb-6">
                  <h4 className="font-semibold text-gray-700 mb-2 text-sm">Détails des erreurs :</h4>
                  <div className="bg-red-50 border border-red-200 rounded-xl p-4 max-h-48 overflow-y-auto">
                    {importResults.errors.map((err, i) => (
                      <p key={i} className="text-xs text-red-600 flex items-start mb-1">
                        <i className="ri-error-warning-line mr-1 mt-0.5 w-4 h-4 flex items-center justify-center flex-shrink-0"></i>
                        {err}
                      </p>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex gap-3 justify-center">
                <button
                  onClick={() => {
                    reset();
                    onSuccess();
                    onClose();
                  }}
                  className="px-6 py-3 bg-teal-600 text-white rounded-full hover:bg-teal-700 font-semibold transition-all shadow-lg whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-check-line mr-2"></i>Terminer
                </button>
                <button
                  onClick={reset}
                  className="px-6 py-3 bg-gray-100 text-gray-700 rounded-full hover:bg-gray-200 font-medium transition-all whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-add-line mr-2"></i>Nouvel import
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}